import moment from "moment";
import { DYED_TYPES, estimatedDispatchTimes, roleIds, routePrefix } from "./constant";

export const formatDate = (date, options = {}) => {
  const { format = "DD/MM/YY", ...opts } = options;
  return moment(date).format(format, opts);
};

export async function downloadFile(url, filename) {
  try {
    const res = await fetch(url, { mode: "cors" });
    if (!res.ok) throw new Error(`HTTP ${res?.status}`);
    const blob = await res.blob();
    const blobUrl = URL.createObjectURL(blob);

    const a = document.createElement("a");
    a.href = blobUrl;
    a.download = filename || url.split("/").pop() || "file.pdf";
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(blobUrl);
  } catch (err) {
    console.log("Error downloading file", err);
    // Fallback: open in new tab if CORS blocks the fetch
    window.open(url, "_blank", "noopener,noreferrer");
  }
}

export const formatAddress = (addr) => {
  if (!addr) return "";
  return [
    addr.address_line1 || addr.address_line_1,
    addr.address_line2 || addr.address_line_2,
    addr.city,
    addr.state,
    addr.post_code || addr.postal_code,
    addr.country,
  ]
    .filter(Boolean)
    .join(", ");
};

const isOccupied = (value, occupiedKeys = new Set()) => occupiedKeys.has(String(value ?? "").trim());

export const calcTotalInStock = (quality_shade, occupiedKeys = new Set()) => {
  const { bsr_stock_cases, folding_stock } = quality_shade || {};

  const totalBsrStock = (bsr_stock_cases ?? []).reduce((sum, stockItem) => {
    if (isOccupied(stockItem?.caseno, occupiedKeys)) return sum;
    return sum + Number(stockItem?.stockqty ?? "0");
  }, 0);

  const totalFoldingStock = (folding_stock ?? []).reduce((sum, stockItem) => {
    if (isOccupied(stockItem?.bar_code, occupiedKeys)) return sum;
    return sum + Number((stockItem?.graded_mtrs || stockItem?.stockqty) ?? "0");
  }, 0);

  return Number(Number(totalBsrStock + totalFoldingStock).toFixed(2));
};

export const calcInStock = (quality_shade, options = {}) => {
  const { occupiedKeys = new Set() } = options;
  // old logic
  // const { bsr_stock_cases, dcl_stockmtrs } = quality_shade || {};
  // return (
  //   (bsr_stock_cases ?? []).reduce(
  //     (sum, stockItem) => sum + Number(stockItem?.stockqty ?? "0"),
  //     0,
  //   ) > Number(dcl_stockmtrs ?? "0")
  // );

  // new logic
  const { bsr_stock_cases, folding_stock } = quality_shade || {};

  const totalInStock = calcTotalInStock(quality_shade, occupiedKeys);

  return totalInStock > 0;
};

export const calcTotalGrey = (quality_shade, occupiedKeys = new Set()) => {
  const { grey_stock } = quality_shade || {};
  const totalGrey = (grey_stock ?? []).reduce((sum, stockItem) => {
    if (isOccupied(stockItem?.pcsno, occupiedKeys)) return sum;
    return sum + Number(stockItem?.dcl_mtrs ?? "0");
  }, 0);
  return Number(Number(totalGrey).toFixed(2));
};

export const calcHasGrey = (quality_shade, options = {}) => {
  const { occupiedKeys = new Set() } = options;
  const totalGrey = calcTotalGrey(quality_shade, occupiedKeys);
  return totalGrey > 0;
};

export const availibilityStatusMap = {
  MTO: { key: "MTO", text: "Make to order", variant: "makeToOrder" },
  IN_STOCK: { key: "IN_STOCK", text: "In Stock", variant: "success" },
  GREY: { key: "GREY", text: "Grey Available", variant: "greyStock" },
};

export const getStockStatus = (quality_shade, options = {}) => {
  let res = availibilityStatusMap.MTO; // default
  if (!quality_shade) return res;

  if (calcInStock(quality_shade, options)) return availibilityStatusMap.IN_STOCK;
  if (calcHasGrey(quality_shade, options)) return availibilityStatusMap.GREY;

  return res;
};

export const roundDecimal = (num, dec = 1) => {
  if (num === null || num === undefined) return 0;
  return Number(num) % 1 === 0 ? Number(num) : Number(Number(num)?.toFixed(dec));
};

/**
 * Converts inches to centimeters.
 * Accepts both numbers and numeric strings.
 * @param {number|string} inches - The length in inches.
 * @returns {number} The equivalent length in centimeters.
 */
export const inchesToCm = (inches) => {
  const formattedInches = Number(inches);
  const defaultValues = {
    36: "90",
    54: "137",
    58: "147",
  };

  if (defaultValues[formattedInches]) {
    return defaultValues[formattedInches];
  }

  const value = parseFloat(inches);

  if (isNaN(value)) {
    return 0;
  }

  return Math.round(value * 2.54);
};

export const handleRouteNav = (route, role_id) => {
  let prefix = "";
  if (role_id === roleIds.Customer) prefix = routePrefix.customer;
  if (role_id === roleIds.Admin) prefix = routePrefix.admin;
  else if (role_id === roleIds.SalesManager || role_id === roleIds.SalesPerson) prefix = routePrefix.sales;
  return `${prefix}${route}`;
};

export const numberFormatter = (raw, { locale = "en-IN" } = {}) => {
  if (raw === null || raw === undefined || raw === "") return 0;

  const n = typeof raw === "number" ? raw : parseFloat(String(raw).replace(/,/g, ""));

  if (!isFinite(n)) return raw;

  const opts = {
    style: "decimal",
    minimumFractionDigits: n % 1 === 0 ? 0 : 2,
    maximumFractionDigits: n % 1 === 0 ? 0 : 2,
  };

  return new Intl.NumberFormat(locale, opts).format(n);
};

export const getSummaryDescription = (currentVariant, allVariants) => {
  if (!currentVariant || !allVariants?.length) return "";

  const isFullyCompleted = allVariants.every((item) => item?.status === "completed");

  if (isFullyCompleted) {
    return "🏆 Congratulations! You’ve achieved your target. If eligible, you may Claim Your Benefits!";
  }

  const percent = currentVariant.progress_percent || 0;
  const isTierOne = currentVariant.index === 1;

  if (isTierOne) {
    if (percent < 25) return "🛒 You haven’t tapped into this scheme yet. Place Orders today!";
    if (percent < 50) return "📈 You’re off to a good start. Keep going!";
    if (percent < 75) return "📊 You’re halfway there! Keep moving, towards your target.";
    return "🚀 You’re almost there! Keep placing orders, and you’ll achieve your target.";
  } else {
    if (percent < 25) return "🔓 You’ve unlocked a new target. Time to start placing orders again!";
    if (percent < 50) return "🌟 You’re off to a great start. Keep progressing, and Claim Higher Benefits!";
    if (percent < 75) return "🧭 You’re halfway there! Keep moving, towards your target.";
    return "🎯 You’re almost there! Keep placing orders, and you’ll achieve your target.";
  }
};

export const showEstimatedDispatchTime = (quality_shade, options = {}) => {
  if (!quality_shade) return estimatedDispatchTimes.generic;
  const { showLabel = false, getLength, skipFullStop = false } = options;

  const times = [];
  const {
    in_stock,
    in_stock_folding,
    in_stock_grey,

    cases_id,
    folding_id,
    grey_id,

    cases_in_stock,
    cases_in_folding,
    cases_in_grey,
    cases_make_to_order,
    total_make_to_order_quantity,

    dye_type,
  } = quality_shade;

  if (Number(in_stock) > 0 || Number(in_stock_folding) > 0 || Number(cases_in_stock) > 0 || Number(cases_in_folding) > 0 || cases_id || folding_id) {
    if (showLabel) {
      times.push(`In Stock ${estimatedDispatchTimes.in_stock}`);
    } else {
      times.push(estimatedDispatchTimes.in_stock);
    }
  }

  if (Number(in_stock_grey) > 0 || Number(cases_in_grey) > 0 || grey_id) {
    if (showLabel) {
      times.push(`Grey ${estimatedDispatchTimes.grey}`);
    } else {
      times.push(estimatedDispatchTimes.grey);
    }
  }

  const hasExplicitMto = Number(cases_make_to_order) > 0 || Number(total_make_to_order_quantity) > 0;

  if (hasExplicitMto || times.length === 0) {
    if (dye_type === DYED_TYPES.top) {
      if (showLabel) {
        times.push(`Make to order ${estimatedDispatchTimes.make_to_order_td}`);
      } else {
        times.push(estimatedDispatchTimes.make_to_order_td);
      }
    } else {
      if (showLabel) {
        times.push(`Make to order ${estimatedDispatchTimes.make_to_order_pd}`);
      } else {
        times.push(estimatedDispatchTimes.make_to_order_pd);
      }
    }
  }

  if (times.length === 0) {
    times.push(estimatedDispatchTimes.generic);
  }

  if (times.length > 0) {
    let uniqueTimes = [...new Set(times)];

    if (getLength) {
      uniqueTimes = uniqueTimes.slice(0, Number(getLength));
    }

    return uniqueTimes.map((t) => t.replace(/\.$/, "")).join(", ") + (skipFullStop ? "" : ".");
  }

  return estimatedDispatchTimes.generic;
};

export const getOrderLongestDispatchTime = (items = []) => {
  if (!items || items.length === 0) return estimatedDispatchTimes.generic;

  // Helper function to safely check if a single item is Make to Order
  const isItemMto = (i) => {
    // Scenario 1: Cart API data (explicit MTO quantities exist)
    if (Number(i.cases_make_to_order) > 0 || Number(i.total_make_to_order_quantity) > 0) {
      return true;
    }

    // Scenario 2: Order API data (relies on the three boolean flags)
    // If we have these keys, and they are ALL exactly 0, it is Make to Order.
    if ("in_stock" in i && "in_stock_folding" in i && "in_stock_grey" in i) {
      if (Number(i.in_stock) === 0 && Number(i.in_stock_folding) === 0 && Number(i.in_stock_grey) === 0) {
        return true;
      }
    }

    return false;
  };

  // 1. Highest Priority (Longest Time): Make to Order - Top Dyed
  const hasMtoTd = items.some((i) => isItemMto(i) && i.dye_type === DYED_TYPES.top);
  if (hasMtoTd) return estimatedDispatchTimes.make_to_order_td;

  // 2. Second Priority: Make to Order - Piece Dyed
  const hasMtoPd = items.some((i) => isItemMto(i) && i.dye_type === DYED_TYPES.piece);
  if (hasMtoPd) return estimatedDispatchTimes.make_to_order_pd;

  // 3. Third Priority: Grey Available
  const hasGrey = items.some((i) => Number(i.in_stock_grey) > 0 || Number(i.cases_in_grey) > 0 || i.grey_id || i.grey_pcsno);
  if (hasGrey) return estimatedDispatchTimes.grey;

  // 4. Shortest Time: In Stock
  return estimatedDispatchTimes.in_stock;
};

export const showAPC = (apc_no) => {
  if (!apc_no) return "";
  return apc_no ? ` - ${typeof apc_no === "string" ? apc_no : "APC"}` : "";
};
